// The help wiki is dev-owned markdown living beside this file. Content is
// bundled at build time via a raw glob rather than fetched from `public/` (the
// pattern the test-scenario docs use), so a topic and the code that links to it
// ship together and a rename cannot 404 in front of a user.
const contentModules = import.meta.glob<string>("./content/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

export interface HelpTopic {
  /** Stable id. Must match the markdown filename, and is what route meta references. */
  id: string;
  title: string;
  /** One line shown in the table of contents. */
  summary: string;
  /** Raw markdown, rendered by HelpSlideOver. */
  body: string;
}

interface HelpSection {
  key: string;
  label: string;
  topics: HelpTopic[];
}

interface HelpTopicDefinition {
  id: string;
  title: string;
  summary: string;
}

const MISSING_CONTENT_BODY = "";

function resolveBody(id: string): string {
  // Deliberately does not throw: a missing help file should not take the app
  // down. helpTopics.test.ts fails the build instead.
  return contentModules[`./content/${id}.md`] ?? MISSING_CONTENT_BODY;
}

function toTopics(definitions: HelpTopicDefinition[]): HelpTopic[] {
  return definitions.map((definition) => ({
    ...definition,
    body: resolveBody(definition.id),
  }));
}

export const helpSections: HelpSection[] = [
  {
    key: "basics",
    label: "Basics",
    topics: toTopics([
      {
        id: "getting-started",
        title: "Getting started",
        summary: "What RediSKU does and the path through it.",
      },
      {
        id: "how-redistribution-works",
        title: "How redistribution works",
        summary: "Donor and receiver POs, movement reasons, and the limits.",
      },
      {
        id: "where-data-comes-from",
        title: "Where the data comes from",
        summary:
          "Which numbers are live, which are per-session, which are samples.",
      },
    ]),
  },
  {
    key: "workflow",
    label: "RediSKU workflow",
    topics: toTopics([
      {
        id: "po-recommendations",
        title: "PO Recommendations",
        summary: "Find and select the POs to rebalance.",
      },
      {
        id: "redistribution-results",
        title: "Redistribution Results",
        summary: "Read the proposed plan and the before/after position.",
      },
      {
        id: "redistribution-calculation",
        title: "Redistribution Calculation",
        summary: "The raw trace behind a run.",
      },
      {
        id: "bulk-order-creation",
        title: "Bulk Order Creation",
        summary: "Find an order and review its SKU lines.",
      },
      {
        id: "intermediate-order-sku-distribution",
        title: "Intermediate Order SKU Distribution",
        summary: "SKU spread across intermediate orders and warehouses.",
      },
    ]),
  },
  {
    key: "insight-iq",
    label: "Insight.IQ",
    topics: toTopics([
      {
        id: "insight-iq",
        title: "Insight.IQ landing page",
        summary: "The control-tower summary, and which parts are sample data.",
      },
      {
        id: "sku-analysis",
        title: "SKU Analysis",
        summary: "Every SKU assessed in the latest run.",
      },
      {
        id: "inventory-health",
        title: "Inventory Health",
        summary: "Before-and-after position at SKU and PO level.",
      },
      {
        id: "transfers",
        title: "Transfers",
        summary: "Movement decisions framed as PO-to-PO transfers.",
      },
      {
        id: "reports",
        title: "Reports",
        summary: "Operational summaries by reason, movement, and PO.",
      },
      {
        id: "algorithm-trace-log",
        title: "Algorithm Trace Log",
        summary: "Run-level diagnostics.",
      },
      {
        id: "settings",
        title: "Settings",
        summary: "Not built yet.",
      },
    ]),
  },
];

export const helpTopics: HelpTopic[] = helpSections.flatMap(
  (section) => section.topics
);

export function findHelpTopic(id?: string): HelpTopic | undefined {
  if (id === undefined) {
    return undefined;
  }
  return helpTopics.find((topic) => topic.id === id);
}

/** Reads `helpTopicId` off route meta, which the router leaves loosely typed. */
export function getRouteHelpTopicId(
  meta: Record<string, unknown>
): string | undefined {
  const helpTopicId = meta.helpTopicId;
  return typeof helpTopicId === "string" ? helpTopicId : undefined;
}
